import { useAtomValue } from 'jotai'
import { ReactNode, useMemo } from 'react'

import { AddressKey, SupportedChainId } from '@cowprotocol/cow-sdk'
import { environmentAtom } from '@cowprotocol/tokens'

import { BalancesCacheUpdater } from './BalancesCacheUpdater'
import { BalancesResetUpdater } from './BalancesResetUpdater'
import { NativeTokenBalanceUpdater } from './NativeTokenBalanceUpdater'

import { useBalancesWatcherSession } from '../hooks/useBalancesWatcherSession'
import { useCustomTokensForChain } from '../hooks/useCustomTokensForChain'
import { useWatcherTokensLists } from '../hooks/useWatcherTokensLists'

export interface BalancesWatcherUpdaterProps {
  account: string | undefined
  chainId: SupportedChainId
  /**
   * True when `chainId` is a bridge target chain (user is browsing tokens of a chain different from their wallet's).
   * In this mode the session is driven exclusively by `bridgeTokenList` - user-imported tokens and enabled token
   * lists are skipped, and an empty `bridgeTokenList` produces no session at all.
   */
  isBridgeMode?: boolean
  /** Bridge buy-tokens fetched from the bridge API. */
  bridgeTokenList?: AddressKey[]
}

const EMPTY_EXCLUDED_TOKENS: Set<string> = new Set()
const EMPTY_BRIDGE_TOKEN_LIST: AddressKey[] = []
const EMPTY_TOKENS_LISTS_URLS: string[] = []
const EMPTY_CUSTOM_TOKENS: AddressKey[] = []

/**
 * Watcher-mode peer of `BalancesAndAllowancesUpdater`. Drives `balancesAtom`
 * via the balances-watcher SSE stream and mounts the same reset/cache
 * subtrees so the atom lifecycle (chain/account reset, localStorage
 * hydration/persistence) stays intact when the LD flag is on.
 */
export function BalancesWatcherUpdater({
  account,
  chainId,
  isBridgeMode = false,
  bridgeTokenList = EMPTY_BRIDGE_TOKEN_LIST,
}: BalancesWatcherUpdaterProps): ReactNode {
  const { tokensListsUrls: unfetchedListsUrls, listsTokens } = useWatcherTokensLists(chainId)
  const userCustomTokens = useCustomTokensForChain(chainId)

  // The token-list / custom-token atoms are keyed on `environmentAtom.chainId`,
  // which lags the wallet chainId by one commit on a chain switch. Gate the
  // session on the two agreeing so we don't POST the previous chain's lists.
  // Bridge mode sources its tokens from `bridgeTokenList` (not the env atom), so
  // it is never desynced.
  const listsChainId = useAtomValue(environmentAtom).chainId
  const isChainSynced = isBridgeMode || listsChainId === chainId

  // Bridge mode drops both token lists and user-imported tokens - the watcher tracks ONLY bridge buy-tokens for the
  // target chain. An empty `bridgeTokenList` produces `(urls=[], customTokens=[])`, which `useBalancesWatcherSession`
  // already treats as "no session" and skips the POST.
  const sessionBody = useMemo(() => {
    if (isBridgeMode) {
      return {
        tokensListsUrls: EMPTY_TOKENS_LISTS_URLS,
        customTokens: bridgeTokenList,
      }
    }
    // Lists already parsed here travel as addresses (see `useWatcherTokensLists`), so they join
    // the user-imported tokens rather than being handed to the watcher as urls to download again
    const customTokens = new Set<AddressKey>(userCustomTokens)

    for (const address of listsTokens) {
      customTokens.add(address)
    }

    return {
      tokensListsUrls: unfetchedListsUrls,
      customTokens: customTokens.size === 0 ? EMPTY_CUSTOM_TOKENS : Array.from(customTokens).sort(),
    }
  }, [isBridgeMode, bridgeTokenList, unfetchedListsUrls, listsTokens, userCustomTokens])

  useBalancesWatcherSession({
    account,
    chainId,
    tokensListsUrls: sessionBody.tokensListsUrls,
    customTokens: sessionBody.customTokens,
    isChainSynced,
  })

  return (
    <>
      <BalancesResetUpdater chainId={chainId} account={account} />
      <BalancesCacheUpdater chainId={chainId} account={account} excludedTokens={EMPTY_EXCLUDED_TOKENS} />
      <NativeTokenBalanceUpdater account={account} chainId={chainId} />
    </>
  )
}
